import { useRouter } from 'next/router';

import { MarketplaceAPI } from 'revibe-api';
import { I18N_NAMESPACES, SUPPORTED_LOCALES } from 'i18n';
import { useTranslation } from 'i18n/hooks';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';
import { SpacedContainer } from 'revibe-ui';
import { BLOG_ROUTES } from 'routes';

import { Page404 } from 'modules/auth/pages/Page404';
import { BlogArticleComments } from 'modules/blog/components/BlogArticleComments';
import { BlogJoinUsBanner } from 'modules/blog/components/BlogJoinUsBanner';
import { BlogPage } from 'modules/blog/components/BlogPage';
import { getDynamicRouteUrl } from 'modules/common/utils';

import { Tag } from 'ui/components/Tag';
import { FacebookIcon } from 'ui/icons/FacebookIcon';
import { LeftArrowIcon } from 'ui/icons/LeftArrowIcon';
import { LinkedInIcon } from 'ui/icons/LinkedInIcon';
import { RightArrowIcon } from 'ui/icons/RightArrowIcon';
import { TwitterIcon } from 'ui/icons/TwitterIcon';

import styles from './BlogArticlePage.module.css';
import Link from 'next/link';
import { BlogLikesButton } from 'modules/blog/components/BlogLikesButton';
import { useEffect } from 'react';

declare global {
  interface Window {
    instgrm: any;
  }
}

type Props = {
  article?: MarketplaceAPI.BlogArticle & {
    likes: number;
    previous?: MarketplaceAPI.BlogArticle | null;
    next?: MarketplaceAPI.BlogArticle | null;
  };
  categories?: MarketplaceAPI.BlogCategory[];
  comments: MarketplaceAPI.BlogArticleComment[];
};

type ShareButtonProps = {
  article?: MarketplaceAPI.BlogArticle & {
    previous?: MarketplaceAPI.BlogArticle | null;
    next?: MarketplaceAPI.BlogArticle | null;
  };
};

const ShareButtons = ({ article }: ShareButtonProps) => {
  const { tu, language } = useTranslation(I18N_NAMESPACES.BLOG);

  if (!article) {
    return null;
  }

  return (
    <div>
      <p className="mb-1 text-xs text-gray-500">{tu('share')}</p>
      <SpacedContainer type="horizontal">
        <FacebookShareButton
          url={`https://www.revibe-upcycling.com/${
            language === SUPPORTED_LOCALES.EN ? '' : `${language}/`
          }blog/${article.category.data.attributes.slug}/${article.slug}`}
          title={article.title}
          quote={`${article.title} | Revibe Blog`}
        >
          <FacebookIcon className="h-6 w-6" />
        </FacebookShareButton>
        <LinkedinShareButton
          url={`https://www.revibe-upcycling.com/${
            language === SUPPORTED_LOCALES.EN ? '' : `${language}/`
          }blog/${article.category.data.attributes.slug}/${article.slug}`}
          title={article.title}
          source="https://www.revibe-upcycling.com"
          summary={article.description}
        >
          <LinkedInIcon className="h-6 w-6" />
        </LinkedinShareButton>
        <TwitterShareButton
          url={`https://www.revibe-upcycling.com/${
            language === SUPPORTED_LOCALES.EN ? '' : `${language}/`
          }blog/${article.category.data.attributes.slug}/${article.slug}`}
          title={article.title}
        >
          <TwitterIcon className="h-6 w-6" />
        </TwitterShareButton>
      </SpacedContainer>
    </div>
  );
};

export const BlogArticlePage = ({ article, categories, comments }: Props) => {
  const router = useRouter();
  const { language, tu } = useTranslation(I18N_NAMESPACES.BLOG);

  useEffect(() => {
    setTimeout(() => {
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      }
    }, 1000);
  }, []);

  if (router.isFallback || !article) {
    return null;
  }

  if (!article || !article.category || !article.author) {
    return <Page404 />;
  }

  return (
    <BlogPage
      title={article.title}
      description={article.description}
      categories={categories}
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:py-14">
        <SpacedContainer>
          <div className="pb-10">
            <SpacedContainer>
              <SpacedContainer spacing="small">
                <Link
                  href={BLOG_ROUTES.HOME}
                  className="font-syne hover:text-green-fluo cursor-pointer text-4xl font-semibold uppercase"
                >
                  Stain
                </Link>
                <SpacedContainer type="horizontal">
                  <Link
                    href={getDynamicRouteUrl(BLOG_ROUTES.CATEGORY, {
                      category: article.category.data.attributes.slug,
                    })}
                  >
                    <Tag isUppercase>
                      {article.category.data.attributes.name}
                    </Tag>
                  </Link>
                  <Tag isUppercase>
                    {new Date(article.createdAt).toLocaleDateString(language, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Tag>
                </SpacedContainer>
              </SpacedContainer>
              <div>
                <h1 className="font-syne max-w-6xl text-2xl font-semibold sm:text-3xl md:text-4xl lg:text-6xl">
                  {article.title}
                </h1>
              </div>
            </SpacedContainer>
          </div>
        </SpacedContainer>
        <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 lg:grid-cols-[min-content_minmax(0,1fr)]">
          <SpacedContainer className="sticky left-0 top-24 m-0 hidden h-fit w-fit whitespace-nowrap lg:flex">
            <div>
              <p className="text-xs">{tu('posted-by')}</p>
              <p className="uppercase">{article.author.data.attributes.name}</p>
            </div>
            <ShareButtons article={article} />
            <BlogLikesButton article={article} />
          </SpacedContainer>
          {article.html && (
            <article
              className={styles.articleContentContainer}
              dangerouslySetInnerHTML={{ __html: article.html }}
            />
          )}
        </div>

        <SpacedContainer className="mx-auto w-full max-w-6xl py-10">
          <SpacedContainer>
            <BlogLikesButton article={article} />
            <BlogArticleComments article={article} comments={comments} />
          </SpacedContainer>
          <div className="flex flex-col justify-between gap-4 bg-gray-50 p-4 md:flex-row">
            <div className="flex grid-cols-2 flex-col items-center gap-4 md:grid">
              <SpacedContainer type="horizontal" centered>
                <div
                  className="h-12 w-12 rounded-full border bg-cover bg-left-top bg-no-repeat"
                  style={{
                    backgroundImage: `url(${article.author.data.attributes?.picture.data?.attributes.formats.thumbnail?.url})`,
                  }}
                />
                <div>
                  <p className="mb-1 text-xs text-gray-500">
                    {tu('posted-by')}
                  </p>
                  <p className="uppercase">
                    {article.author.data.attributes.name}
                  </p>
                </div>
              </SpacedContainer>
              <div>
                <p className="mb-1 text-xs text-gray-500">Tags</p>
                <p className="uppercase">
                  <Link
                    href={getDynamicRouteUrl(BLOG_ROUTES.CATEGORY, {
                      category: article.category.data.attributes.slug,
                    })}
                  >
                    <Tag isUppercase>
                      {article.category.data.attributes.name}
                    </Tag>
                  </Link>
                </p>
              </div>
            </div>
            <ShareButtons article={article} />
          </div>
          <div className="mx-auto w-full max-w-6xl">
            <BlogJoinUsBanner />
          </div>
        </SpacedContainer>

        <SpacedContainer>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {article.previous ? (
              <Link
                href={getDynamicRouteUrl(BLOG_ROUTES.ARTICLE, {
                  category: article.previous.category.data.attributes.slug,
                  slug: article.previous.slug,
                })}
                className="hover:bg-green-fluo inline-flex items-center gap-4 border border-black p-4"
              >
                <LeftArrowIcon className="h-6 w-6 stroke-black" />
                <div className="w-full">
                  <p className="text-lg font-semibold">
                    {tu('previous-article')}
                  </p>
                  <p className="text-gray-500">{article.previous.title}</p>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {article.next ? (
              <Link
                href={getDynamicRouteUrl(BLOG_ROUTES.ARTICLE, {
                  category: article.next.category.data.attributes.slug,
                  slug: article.next.slug,
                })}
                className="hover:bg-green-fluo inline-flex items-center gap-4 border border-black p-4"
              >
                <div className="w-full">
                  <p className="text-lg font-semibold">{tu('next-article')}</p>
                  <p className="text-gray-500">{article.next.title}</p>
                </div>
                <RightArrowIcon className="h-6 w-6 stroke-black" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </SpacedContainer>
      </div>
    </BlogPage>
  );
};
